(() => {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger) return;

  gsap.registerPlugin(ScrollTrigger);

  const registry = window.__pegasiMotionRegistry || {
    bound: false,
    controllers: new WeakMap(),
    roots: new Set(),
  };
  window.__pegasiMotionRegistry = registry;

  const rootsIn = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('.pgx-landing')) roots.push(scope);
    scope.querySelectorAll?.('.pgx-landing').forEach((root) => roots.push(root));
    return roots;
  };

  // Writing a custom property invalidates style for every descendant consuming
  // it, so quantise to sub-visible precision and skip the write when nothing
  // changed. Most per-frame writes disappear once the tone has settled, even
  // while scrolling continues.
  const navRenderState = new WeakMap();
  const renderNav = (nav, state) => {
    const channel = Math.round(255 + ((27 - 255) * state.foreground));
    const surface = Math.round(state.surface * 1000) / 1000;
    const ratio = Math.round(state.foreground * 1000) / 1000;
    const last = navRenderState.get(nav);
    if (last && last.channel === channel && last.surface === surface && last.ratio === ratio) return;
    navRenderState.set(nav, { channel, surface, ratio });
    nav.style.setProperty('--pgx-nav-rgb', `${channel}, ${channel}, ${channel}`);
    nav.style.setProperty('--pgx-nav-surface', surface.toFixed(4));
    nav.style.setProperty('--pgx-nav-ratio', ratio.toFixed(4));
  };

  const createAdaptiveNav = (root, reduceMotion) => {
    const nav = root.querySelector('[data-pgx-nav]');
    const toneElements = [...root.querySelectorAll('[data-pgx-nav-tone]')];
    if (!nav || !toneElements.length) return { cleanup() {}, sync() {} };

    const state = { foreground: 0, surface: 0 };
    const target = { foreground: 0, surface: 0 };
    let zones = [];
    let navHeight = 72;
    let lastScrollY = window.scrollY;
    let ticking = false;

    const cache = () => {
      const pageY = window.scrollY;
      navHeight = nav.getBoundingClientRect().height || 72;
      zones = toneElements.map((element) => {
        const rect = element.getBoundingClientRect();
        return {
          bottom: rect.bottom + pageY,
          light: element.dataset.pgxNavTone === 'light',
          top: rect.top + pageY,
        };
      });
    };

    const sampleBand = (bandTop, bandBottom) => {
      const top = window.scrollY + bandTop;
      const bottom = window.scrollY + bandBottom;
      let light = 0;
      let total = 0;

      zones.forEach((zone) => {
        const overlap = Math.max(0, Math.min(bottom, zone.bottom) - Math.max(top, zone.top));
        if (!overlap) return;
        total += overlap;
        if (zone.light) light += overlap;
      });

      return total ? light / total : state.foreground;
    };

    const stopTicker = () => {
      if (!ticking) return;
      gsap.ticker.remove(tick);
      ticking = false;
    };

    const tick = (_time, deltaMs) => {
      const deltaSeconds = Math.min(deltaMs / 1000, .064);
      const amount = 1 - Math.exp(-18 * deltaSeconds);
      state.foreground += (target.foreground - state.foreground) * amount;
      state.surface += (target.surface - state.surface) * amount;

      if (Math.abs(target.foreground - state.foreground) < .001) state.foreground = target.foreground;
      if (Math.abs(target.surface - state.surface) < .001) state.surface = target.surface;
      renderNav(nav, state);

      if (state.foreground === target.foreground && state.surface === target.surface) stopTicker();
    };

    const startTicker = () => {
      if (ticking) return;
      ticking = true;
      gsap.ticker.add(tick);
    };

    const sync = (immediate = false) => {
      const scrollDelta = Math.abs(window.scrollY - lastScrollY);
      lastScrollY = window.scrollY;
      target.surface = sampleBand(0, navHeight);
      const bandTop = Math.min(24, navHeight);
      const bandBottom = Math.min(48, navHeight);
      target.foreground = sampleBand(bandTop, Math.max(bandTop + 1, bandBottom));

      const unsafeContrastJump = Math.abs(target.foreground - state.foreground) > .65 || scrollDelta > navHeight * 1.25;
      if (immediate || reduceMotion || unsafeContrastJump) {
        stopTicker();
        state.foreground = target.foreground;
        state.surface = target.surface;
        renderNav(nav, state);
        return;
      }

      startTicker();
    };

    cache();
    sync(true);

    const scrollTrigger = ScrollTrigger.create({
      end: 'max',
      onRefresh: () => {
        cache();
        sync(true);
      },
      onUpdate: () => sync(false),
      start: 0,
    });

    return {
      cleanup() {
        stopTicker();
        scrollTrigger.kill();
      },
      sync(immediate = true) {
        cache();
        sync(immediate);
      },
    };
  };

  const clearMotionProps = (targets) => {
    gsap.set(targets.filter(Boolean), { clearProps: 'transform,opacity,visibility,willChange' });
  };

  const createHeroIntro = (root, shouldPlay = true, onSettled = () => {}) => {
    const nav = root.querySelector('[data-pgx-nav]');
    const media = root.querySelector('.pgx-hero__media > img');
    const titleLines = [...root.querySelectorAll('[data-pgx-hero-line]')];
    const copy = root.querySelector('.pgx-hero__copy');
    const buttons = [...root.querySelectorAll('.pgx-hero__buttons .pgx-button')];
    const scrollLabel = root.querySelector('.pgx-hero__scroll');
    const animated = [nav, media, ...titleLines, copy, ...buttons, scrollLabel].filter(Boolean);

    if (!shouldPlay || window.scrollY > 24) {
      root.classList.remove('pgx-motion-pending');
      clearMotionProps(animated);
      onSettled();
      return { active: false, cleanup() {}, handoff() {} };
    }

    let active = true;
    root.classList.remove('pgx-motion-pending');
    gsap.set(animated, { willChange: 'transform,opacity' });
    gsap.set(media, { autoAlpha: .72, scale: 1.065 });
    gsap.set(nav, { autoAlpha: 0, y: -8 });
    gsap.set(titleLines, { autoAlpha: 0, yPercent: 110 });
    gsap.set(copy, { autoAlpha: 0, y: 16 });
    gsap.set(buttons, { autoAlpha: 0, y: 12 });
    gsap.set(scrollLabel, { autoAlpha: 0 });
    const timeline = gsap.timeline({
      defaults: { ease: 'power4.out' },
      onComplete: () => {
        active = false;
        clearMotionProps(animated);
        onSettled();
      },
    })
      .addLabel('image', 0)
      .to(media, { autoAlpha: 1, duration: 1.25, ease: 'power2.out', scale: 1 }, 'image')
      .to(nav, { autoAlpha: 1, duration: .42, y: 0 }, 'image+=.06')
      .addLabel('type', .18)
      .to(titleLines, { autoAlpha: 1, duration: .82, stagger: .07, yPercent: 0 }, 'type')
      .to(copy, { autoAlpha: 1, duration: .56, y: 0 }, 'type+=.28')
      .to(buttons, { autoAlpha: 1, duration: .48, stagger: .06, y: 0 }, 'type+=.36')
      .to(scrollLabel, { autoAlpha: 1, duration: .38 }, 'type+=.62');

    // The preloader (snippets/pegasi-loader.liquid) covers the viewport on first
    // paint. Without this the intro would play out of sight underneath it and the
    // hero would simply appear already-settled. Hold, then run on its signal —
    // with a timed release so a loader that never reports cannot strand the hero.
    if (document.documentElement.classList.contains('pgx-loading')) {
      timeline.pause();
      const release = () => {
        if (active && timeline.paused()) timeline.play();
      };
      window.addEventListener('pegasi:loaded', release, { once: true });
      gsap.delayedCall(5, release);
    }

    const settle = () => {
      if (!active) return;
      active = false;
      timeline.kill();
      clearMotionProps(animated);
      onSettled();
    };

    return {
      get active() { return active; },
      cleanup() {
        active = false;
        timeline.kill();
        clearMotionProps(animated);
      },
      handoff: settle,
    };
  };

  const createSectionMotion = (root, { deferHero = false } = {}) => {
    const hero = root.querySelector('.pgx-hero');
    const heroMedia = hero?.querySelector('.pgx-hero__media > img');
    const heroContent = hero?.querySelector('.pgx-hero__content');
    const heroScroll = hero?.querySelector('.pgx-hero__scroll');
    let heroTimeline = null;
    const enableHero = () => {
      if (heroTimeline || !hero || !heroMedia || !heroContent) return;
      heroTimeline = gsap.timeline({
        scrollTrigger: { end: 'bottom top', scrub: .35, start: 'top top', trigger: hero },
      })
        .to(heroMedia, { ease: 'none', scale: 1.055, yPercent: 3 }, 0)
        .to(heroContent, { autoAlpha: .35, ease: 'none', yPercent: -10 }, 0)
        .to(heroScroll, { autoAlpha: 0, ease: 'none' }, 0);
    };
    if (!deferHero) enableHero();

    const genesis = root.querySelector('.pgx-genesis');
    const genesisBase = genesis?.querySelector('.pgx-genesis__media img:first-child');
    const panel = genesis?.querySelector('.pgx-genesis__panel');
    const genesisTitle = panel?.querySelector('h2');
    const genesisCopy = panel?.querySelector('p');
    const genesisRule = panel?.querySelector('img');
    const genesisLink = panel?.querySelector('a');

    if (genesis && genesisBase) {
      gsap.fromTo(genesisBase,
        { scale: 1.075, yPercent: -2 },
        {
          ease: 'none',
          scale: 1.015,
          scrollTrigger: { end: 'bottom top', scrub: .42, start: 'top bottom', trigger: genesis },
          yPercent: 2,
        });
    }

    if (genesis && panel) {
      const genesisAnimated = [panel, genesisTitle, genesisCopy, genesisRule, genesisLink].filter(Boolean);
      gsap.timeline({
        defaults: { ease: 'power4.out' },
        scrollTrigger: { once: true, start: 'top 66%', trigger: genesis },
      })
        .set(genesisAnimated, { willChange: 'transform,opacity' })
        .from(panel, { autoAlpha: 0, duration: .85, x: -24 }, 0)
        .from(genesisTitle, { autoAlpha: 0, duration: .76, y: 22 }, .08)
        .from(genesisCopy, { autoAlpha: 0, duration: .56, y: 10 }, .17)
        .from(genesisRule, { duration: .7, scaleX: 0, transformOrigin: 'left center' }, .23)
        .from(genesisLink, { autoAlpha: 0, duration: .52, x: -8 }, .31)
        .call(() => clearMotionProps(genesisAnimated));
    }

    const reports = root.querySelector('.pgx-reports');
    const reportsEyebrow = reports?.querySelector('.pgx-reports__eyebrow');
    const reportsTitle = reports?.querySelector(':scope > h2');
    const cards = reports ? [...reports.querySelectorAll('.pgx-report')] : [];
    if (reports && reportsTitle && cards.length) {
      const reportsAnimated = [reportsEyebrow, reportsTitle, ...cards].filter(Boolean);
      gsap.timeline({
        defaults: { ease: 'power4.out' },
        scrollTrigger: { once: true, start: 'top 76%', trigger: reports },
      })
        .set(reportsAnimated, { willChange: 'transform,opacity' })
        .from(reportsEyebrow, { autoAlpha: 0, duration: .52, x: -12 }, 0)
        .from(reportsTitle, { autoAlpha: 0, duration: .92, y: 34 }, .06)
        .from(cards, { autoAlpha: 0, duration: .82, stagger: .08, y: 30 }, .22)
        .call(() => clearMotionProps(reportsAnimated));
    }

    const footer = root.querySelector('.pgx-footer');
    const motto = footer?.querySelector('.pgx-footer__motto');
    const columns = footer ? [...footer.querySelectorAll('.pgx-footer__column')] : [];
    const wordmark = footer?.querySelector('.pgx-footer__wordmark');
    const bottom = footer?.querySelector('.pgx-footer__bottom');
    if (footer) {
      const footerAnimated = [motto, ...columns, wordmark, bottom].filter(Boolean);
      gsap.timeline({
        defaults: { ease: 'power4.out' },
        scrollTrigger: { once: true, start: 'top 82%', trigger: footer },
      })
        .set(footerAnimated, { willChange: 'transform,opacity' })
        .from(motto, { autoAlpha: 0, duration: .7, y: 18 }, 0)
        .from(columns, { autoAlpha: 0, duration: .56, stagger: .075, y: 12 }, .08)
        .from(wordmark, { autoAlpha: 0, duration: .7 }, .18)
        .from(bottom, { autoAlpha: 0, duration: .48, y: 8 }, .28)
        .call(() => clearMotionProps(footerAnimated));
    }

    return {
      cleanup() {
        if (heroTimeline) {
          heroTimeline.kill();
          heroTimeline = null;
        }
      },
      enableHero,
    };
  };

  // Field reports carousel.
  //
  // Arrow buttons plus native horizontal scroll. This was pointer-drag, which
  // required preventDefault() on pointerdown — and that swallowed every click
  // meant for a card's video controls, so play, scrub and volume were all
  // unreliable once the cards carried video. Trackpad and touch scroll the track
  // natively; the arrows are the pointer affordance, and the track keeps its own
  // arrow-key and Home/End handling for keyboards.
  const createReportsCarousel = (root, { reduceMotion }) => {
    const track = root.querySelector('.pgx-reports__track[data-pgx-reports-track]');
    const progress = root.querySelector('[data-pg-progress]');
    if (!track || !progress) return () => {};

    const abort = new AbortController();
    const { signal } = abort;
    const progressX = gsap.quickSetter(progress, 'xPercent');
    const prevButton = root.querySelector('[data-pgx-reports-prev]');
    const nextButton = root.querySelector('[data-pgx-reports-next]');
    const viewport = root.querySelector('[data-pgx-reports-viewport]');
    let thumbTravel = ((1 - .490625) / .490625) * 100;
    let metricsFrame = 0;
    let edgeFrame = 0;
    // offsetLeft/offsetWidth are cached on metrics rather than read per scroll
    // frame — getBoundingClientRect() per card per frame forces a layout while
    // the row is moving, which is exactly when it can least afford one.
    let cardMetrics = [];

    const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const syncArrows = () => {
      const max = maxScroll();
      // 1px of tolerance: fractional scrollLeft on high-DPI displays otherwise
      // leaves the end arrow enabled with nowhere left to travel.
      const atStart = track.scrollLeft <= 1;
      const atEnd = track.scrollLeft >= max - 1;
      if (prevButton) prevButton.disabled = atStart;
      if (nextButton) nextButton.disabled = atEnd;
      if (viewport) {
        // A gradient only appears on a side that still has something hidden.
        if (!atStart && max > 0) viewport.setAttribute('data-can-start', '');
        else viewport.removeAttribute('data-can-start');
        if (!atEnd && max > 0) viewport.setAttribute('data-can-end', '');
        else viewport.removeAttribute('data-can-end');
      }
    };

    // Dim and shrink each card by how far past the track's edges it sits, so the
    // row reads with depth as it slides instead of translating flat.
    const paintEdges = () => {
      edgeFrame = 0;
      if (reduceMotion || !cardMetrics.length) return;
      const viewLeft = track.scrollLeft;
      const viewRight = viewLeft + track.clientWidth;
      const feather = Math.max(80, track.clientWidth * .18);

      cardMetrics.forEach(({ el, left, width }) => {
        const right = left + width;
        // How far the card has crossed either edge, 0 while fully inside.
        const past = Math.max(viewLeft - left, right - viewRight, 0);
        const ratio = Math.min(1, past / feather);
        el.style.setProperty('--pgx-edge-opacity', (1 - (ratio * .62)).toFixed(3));
        el.style.setProperty('--pgx-edge-scale', (1 - (ratio * .035)).toFixed(4));
      });
    };

    const queueEdges = () => {
      if (!edgeFrame) edgeFrame = window.requestAnimationFrame(paintEdges);
    };

    const updateProgress = () => {
      const max = maxScroll();
      progressX(max ? (track.scrollLeft / max) * thumbTravel : 0);
      syncArrows();
      queueEdges();
    };

    const updateMetrics = () => {
      cardMetrics = [...track.querySelectorAll('.pgx-report')].map((el) => ({
        el,
        left: el.offsetLeft,
        width: el.offsetWidth,
      }));
      const pages = Math.max(1, Math.ceil(track.scrollWidth / Math.max(1, track.clientWidth)));
      const thumbRatio = pages === 1 ? 1 : (pages === 2 ? .490625 : 1 / pages);
      thumbTravel = thumbRatio < 1 ? ((1 - thumbRatio) / thumbRatio) * 100 : 0;
      progress.style.setProperty('--pgx-progress-ratio', `${(thumbRatio * 100).toFixed(4)}%`);
      updateProgress();
    };

    const queueMetrics = () => {
      if (metricsFrame) return;
      metricsFrame = window.requestAnimationFrame(() => {
        metricsFrame = 0;
        updateMetrics();
      });
    };

    const animateScroll = (value) => {
      const target = gsap.utils.clamp(0, maxScroll(), value);
      if (reduceMotion) {
        gsap.killTweensOf(track, 'scrollLeft');
        track.scrollLeft = target;
        updateProgress();
        return;
      }
      const distance = Math.abs(target - track.scrollLeft);
      gsap.to(track, {
        duration: gsap.utils.clamp(.28, .7, .25 + (distance / 1000)),
        ease: 'power3.out',
        overwrite: true,
        scrollLeft: target,
      });
    };

    const setScroll = (value) => {
      gsap.killTweensOf(track, 'scrollLeft');
      track.scrollLeft = gsap.utils.clamp(0, maxScroll(), value);
      updateProgress();
    };

    // One card plus the gap, so an arrow press lands the next card on the same
    // edge the current one occupied rather than a fraction of the viewport.
    const stepDistance = () => {
      const card = track.querySelector('.pgx-report');
      const gap = Number.parseFloat(getComputedStyle(track).gap) || 0;
      return card ? card.getBoundingClientRect().width + gap : track.clientWidth * .8;
    };

    // Only one testimonial plays at a time. The overlay state is driven off real
    // media events, not the click, so it stays correct when the native controls
    // are used instead of the big play button.
    const stopOtherVideos = (keep) => {
      track.querySelectorAll('.pgx-report__video').forEach((video) => {
        if (video !== keep && !video.paused) video.pause();
      });
    };

    // Click-to-play bound to the video itself. Nothing is layered over it: an
    // overlay button covered the control bar and broke pause, scrub, volume and
    // fullscreen, but removing it outright left no way to start playback at all,
    // because Chrome does not toggle a video when its body is clicked.
    //
    // Clicks on the native control bar retarget to the video and so land here
    // too, which would double-toggle — play, then immediately pause. Hence the
    // bottom strip is ignored and left to the controls. No preventDefault
    // anywhere, so nothing native is ever suppressed.
    const CONTROL_BAR = 56;

    track.querySelectorAll('[data-pgx-report-media]').forEach((media) => {
      const video = media.querySelector('.pgx-report__video');
      if (!video) return;

      video.addEventListener('click', (event) => {
        const rect = video.getBoundingClientRect();
        // The bottom strip belongs to the native control bar: no toggle, no
        // preventDefault, nothing — those clicks are entirely the browser's.
        if (event.clientY > rect.bottom - CONTROL_BAR) return;
        // Chrome also toggles playback itself when the video body is clicked, so
        // without this the click acted twice — this handler started the video and
        // the native toggle paused it in the same instant, which presented as
        // "play does nothing". preventDefault suppresses the native half; this
        // handler is then the only actor, and it is what keeps body-clicks
        // working in Firefox and Safari, which have no native body toggle.
        event.preventDefault();
        if (video.paused) {
          stopOtherVideos(video);
          video.play()?.catch?.((error) => {
            console.warn('[pegasi] testimonial video failed to play:', error?.message || error, video.currentSrc);
          });
        } else {
          video.pause();
        }
      }, { signal });

      video.addEventListener('error', () => {
        // Named mediaError, not media — the enclosing scope already has a media
        // element in hand and shadowing it here reads as a bug.
        const mediaError = video.error;
        console.warn('[pegasi] testimonial video error:', mediaError?.code, mediaError?.message, video.currentSrc);
      }, { signal });

      video.addEventListener('play', () => {
        media.classList.add('has-started');
        stopOtherVideos(video);
      }, { signal });
    });

    prevButton?.addEventListener('click', () => animateScroll(track.scrollLeft - stepDistance()), { signal });
    nextButton?.addEventListener('click', () => animateScroll(track.scrollLeft + stepDistance()), { signal });

    track.addEventListener('scroll', updateProgress, { passive: true, signal });
    window.addEventListener('resize', queueMetrics, { passive: true, signal });
    track.addEventListener('wheel', () => gsap.killTweensOf(track, 'scrollLeft'), { passive: true, signal });

    track.addEventListener('keydown', (event) => {
      const step = stepDistance();
      if (event.key === 'ArrowLeft') setScroll(track.scrollLeft - step);
      else if (event.key === 'ArrowRight') setScroll(track.scrollLeft + step);
      else if (event.key === 'Home') setScroll(0);
      else if (event.key === 'End') setScroll(maxScroll());
      else return;
      event.preventDefault();
    }, { signal });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(queueMetrics);
      observer.observe(track);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }

    updateMetrics();

    return () => {
      abort.abort();
      if (metricsFrame) window.cancelAnimationFrame(metricsFrame);
      if (edgeFrame) window.cancelAnimationFrame(edgeFrame);
      gsap.killTweensOf(track);
      cardMetrics.forEach(({ el }) => {
        el.style.removeProperty('--pgx-edge-opacity');
        el.style.removeProperty('--pgx-edge-scale');
      });
      viewport?.removeAttribute('data-can-start');
      viewport?.removeAttribute('data-can-end');
    };
  };

  const setReducedMotionState = (root) => {
    root.classList.remove('pgx-motion-pending');
    clearMotionProps([
      root.querySelector('[data-pgx-nav]'),
      root.querySelector('.pgx-hero__media > img'),
      ...root.querySelectorAll('[data-pgx-hero-line], .pgx-hero__copy, .pgx-button, .pgx-genesis__panel, .pgx-genesis__copy > *, .pgx-reports > h2, .pgx-report, .pgx-footer__motto, .pgx-footer__column, .pgx-footer__wordmark, .pgx-footer__bottom'),
    ]);
  };

  const createMobileMenu = (root) => {
    const button = root.querySelector('.pgx-nav__menu');
    const menu = root.querySelector('.pgx-nav__links');
    if (!button || !menu) return () => {};
    const abort = new AbortController();
    const { signal } = abort;

    const close = (returnFocus = false) => {
      root.classList.remove('is-menu-open');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-label', 'Open menu');
      if (returnFocus) button.focus({ preventScroll: true });
    };

    button.addEventListener('click', () => {
      const open = root.classList.toggle('is-menu-open');
      button.setAttribute('aria-expanded', String(open));
      button.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      if (open) menu.querySelector('a')?.focus({ preventScroll: true });
    }, { signal });
    menu.addEventListener('click', (event) => {
      if (event.target.closest('a')) close();
    }, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && root.classList.contains('is-menu-open')) close(true);
    }, { signal });
    window.addEventListener('resize', () => {
      if (window.innerWidth > 760) close();
    }, { passive: true, signal });

    return () => {
      close();
      abort.abort();
    };
  };

  const createController = (root) => {
    const media = gsap.matchMedia();
    let destroyed = false;
    let introHandled = false;
    let syncNav = () => {};
    const cleanupMenu = createMobileMenu(root);

    media.add({
      active: '(min-width: 0px)',
      reduceMotion: '(prefers-reduced-motion: reduce)',
    }, (context) => {
      const { reduceMotion } = context.conditions;
      const navController = createAdaptiveNav(root, reduceMotion);
      syncNav = navController.sync;
      const cleanupDrag = createReportsCarousel(root, { reduceMotion });
      let cleanupSections = () => {};
      let cleanupIntro = () => {};
      let removeIntroHandoff = () => {};

      if (reduceMotion) {
        setReducedMotionState(root);
        introHandled = true;
      }
      else {
        const sections = createSectionMotion(root, { deferHero: true });
        cleanupSections = sections.cleanup;
        const intro = createHeroIntro(root, !introHandled, sections.enableHero);
        cleanupIntro = intro.cleanup;
        const handoffOnScroll = () => {
          if (window.scrollY <= 24) return;
          intro.handoff();
          removeIntroHandoff();
        };
        removeIntroHandoff = () => window.removeEventListener('scroll', handoffOnScroll);
        if (intro.active) window.addEventListener('scroll', handoffOnScroll, { passive: true });
        else sections.enableHero();
        introHandled = true;
      }

      return () => {
        removeIntroHandoff();
        cleanupIntro();
        cleanupSections();
        cleanupDrag();
        navController.cleanup();
      };
    }, root);

    const refreshCall = gsap.delayedCall(.08, () => {
      if (!destroyed) ScrollTrigger.refresh();
    }).pause();
    const resizeObserver = 'ResizeObserver' in window
      ? new ResizeObserver(() => refreshCall.restart(true))
      : { disconnect() {} };
    resizeObserver.observe?.(root);

    // Was: one full ScrollTrigger.refresh() per image load, undebounced.
    // refresh() forces a synchronous full-page layout, so a gallery of large
    // images caused one layout thrash per arriving file. Coalesce them into a
    // single refresh instead (same approach already used in pegasi-flow.js).
    const imageRefresh = gsap.delayedCall(.12, () => {
      if (!destroyed) ScrollTrigger.refresh();
    }).pause();
    root.querySelectorAll('img').forEach((image) => {
      if (image.complete) return;
      const queue = () => imageRefresh.restart(true);
      image.addEventListener('load', queue, { once: true });
      image.addEventListener('error', queue, { once: true });
    });

    document.fonts?.ready.then(() => {
      if (!destroyed && root.isConnected) ScrollTrigger.refresh();
    });

    return {
      destroy() {
        destroyed = true;
        resizeObserver.disconnect();
        refreshCall.kill();
        imageRefresh.kill();
        cleanupMenu();
        media.revert();
      },
      refresh() {
        if (destroyed) return;
        syncNav(true);
        ScrollTrigger.refresh();
      },
    };
  };

  const mount = (root) => {
    if (registry.controllers.has(root)) return;
    const controller = createController(root);
    registry.controllers.set(root, controller);
    registry.roots.add(root);
  };

  const unmount = (root) => {
    const controller = registry.controllers.get(root);
    if (!controller) return;
    controller.destroy();
    registry.controllers.delete(root);
    registry.roots.delete(root);
  };

  const mountScope = (scope) => rootsIn(scope).forEach(mount);
  const unmountScope = (scope) => rootsIn(scope).forEach(unmount);

  if (!registry.bound) {
    registry.bound = true;
    document.addEventListener('shopify:section:load', (event) => mountScope(event.target));
    document.addEventListener('shopify:section:unload', (event) => unmountScope(event.target));
    window.addEventListener('pageshow', () => {
      registry.roots.forEach((root) => registry.controllers.get(root)?.refresh());
    });
  }

  mountScope(document);
})();
